/**
 * HOME CULPRITS — the pin-offset measurer (Kyle dictation, 2026-08-04).
 *
 * Does exactly one thing: measures the three items and publishes what CSS
 * cannot work out for itself — the stack's total height and where each item's
 * content sits inside its own box. It positions nothing, animates nothing, and
 * listens to no scroll event; `position: sticky` in
 * section-eg-home-culprits.css does all of the actual pinning, on every frame,
 * in the compositor, whether this file runs or not.
 *
 * WHY IT EXISTS AT ALL, given the geometry is arithmetic:
 *
 *     off(1) = 0
 *     off(n) = off(n−1) + h(n−1) + gap
 *     stack  = Σ h + 2 × gap
 *
 * — every term is available in CSS EXCEPT the heights, and CSS has no way to
 * ask for a sibling element's height. The three items are not the same height
 * (item 1's paragraph sets to two lines, items 2 and 3 to three), so a single
 * nominal height in the tokens cannot produce two true 100px gaps. It can only
 * produce approximately-right ones. That approximation is exactly what the
 * tokens ship as the default, and this file replaces it with the measured
 * truth.
 *
 * WHERE THE STACK IS THEN CENTRED IS NOT THIS FILE'S BUSINESS. --eg-hc-top is
 * derived in CSS from the stack height written below, so the centring — and
 * the band the non-scrolling nav bar takes off the top of the window — is
 * stated once, and the no-JS path is centred by the same expression.
 *
 * FAIL-CORRECT, NOT FAIL-VISIBLE, and the distinction matters here. The panel's
 * script can fail and leave a finished-looking sentence because its default IS
 * the end state. This one has no end state to fall back to — so the tokens'
 * defaults are built off the TALLEST item, which errs the gaps wide rather than
 * narrow. Wide costs slack under the shorter item; narrow would overlap two
 * paragraphs, which is the one failure a reader cannot read past. If this file
 * never runs, the sequence still stacks in order and nothing collides.
 *
 * MOTION PREFERENCE IS NOT CHECKED, ON PURPOSE. Sticky positioning is layout,
 * not animation — the reduced-motion end state is the same stack in the same
 * place — and measuring is not motion either. Nothing here is gated because
 * nothing here is a preference to honour.
 */
(function () {
  var host = document.querySelector('.eg-homeculprits-host');
  if (!host) return;

  var items = host.querySelectorAll('[data-eg-hc-item]');
  if (!items.length) return;

  /* The gap is read from the CASCADE rather than hardcoded, so the mobile
     block's smaller --eg-hc-stack-gap is honoured without this file knowing
     that block exists. Read off an ITEM, not off :root — the media query
     overrides the token on .eg-homeculprits, and only a descendant sees that. */
  function gap() {
    var raw = getComputedStyle(items[0]).getPropertyValue('--eg-hc-stack-gap');
    var px = parseFloat(raw);
    return isNaN(px) ? 0 : px;
  }

  /* THE ITEM'S OWN BOX IS NOT ITS HEIGHT, and forgetting that would make this
     file measure its own output. Every item box is deliberately as tall as the
     whole finished stack (--eg-hc-stack-h — see the pin geometry in
     evergreen-tokens.css), so `items[i].getBoundingClientRect()` returns the
     number this function is trying to CALCULATE, and the stack would grow by
     itself on every resize.

     What is wanted is the height of the item's CONTENT — so this measures the
     UNION of its two boxes, the illustration and the text column: lowest
     bottom minus highest top.

     THE UNION, NOT THE TALLER OF THE TWO, and the difference is not academic.
     Taking `Math.max` of the two heights is right only while they sit SIDE BY
     SIDE. The mobile block puts the illustration ABOVE the text, where the
     item is as tall as both plus the row gap — and the max was two thirds of
     that, so every offset came out short and the three items overlapped each
     other by ~50px at 390×844 (measured 2026-08-04). The union is correct in
     both layouts and in any future one, because it asks where the content
     actually starts and stops instead of assuming an arrangement. */
  function contentHeight(item) {
    var parts = item.querySelectorAll('.eg-homeculprits__illus, .eg-homeculprits__text');
    var top = Infinity;
    var bottom = -Infinity;
    for (var i = 0; i < parts.length; i += 1) {
      var box = parts[i].getBoundingClientRect();
      /* A `display: none` part reports an ALL-ZERO rect, which is not a box at
         the top of the document — it is the absence of one, and folding it into
         the union would drag `top` to 0 and inflate every item by however far
         down the page it sits. The illustrations are hidden as of 2026-08-08
         (see section-eg-home-culprits.css); this guard is what keeps that a
         purely visual change. Skipping zero-area parts is correct in general:
         a part that occupies nothing contributes nothing to the content's
         extent. */
      if (box.width === 0 && box.height === 0) continue;
      if (box.top < top) top = box.top;
      if (box.bottom > bottom) bottom = box.bottom;
    }
    return bottom > top ? bottom - top : 0;
  }

  function measure() {
    var g = gap();
    var heights = [];
    var stack = 0;
    var i;

    /* getBoundingClientRect, not offsetHeight: offsetHeight rounds to whole
       pixels, and three roundings accumulate into a visibly wrong gap by the
       third item. The rect is fractional and the sticky offsets are too.

       Reading every height BEFORE writing any property keeps this to one
       layout pass — interleaving reads and writes would force a reflow per
       item. */
    for (i = 0; i < items.length; i += 1) {
      heights.push(contentHeight(items[i]));
      stack += heights[i];
    }
    stack += g * (items.length - 1);

    /* THE STACK HEIGHT IS ALL THE CENTRING NEEDS FROM HERE. --eg-hc-top is
       computed in CSS off this value, so the formula — including the band the
       non-scrolling nav bar takes off the top of the window — lives in exactly
       one place and the no-JS path gets the same treatment. Writing `top` here
       as well meant the same arithmetic in two languages. */
    host.style.setProperty('--eg-hc-stack-h', stack.toFixed(2) + 'px');

    /* Offsets are cumulative from the top of the stack, so item 1 is always 0
       and the last one always lands exactly (its own height) short of the
       stack height — which is what makes the boxes fit their content with no
       slack at the bottom. */
    var off = 0;
    for (i = 0; i < items.length; i += 1) {
      host.style.setProperty('--eg-hc-off-' + (i + 1), off.toFixed(2) + 'px');
      off += heights[i] + g;
    }
  }

  /* rAF-coalesced. `resize` fires far faster than the page paints, and every
     handler here ends in a batch of style writes. */
  var queued = false;

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      measure();
    });
  }

  measure();
  window.addEventListener('resize', schedule);

  /* WEBFONTS CHANGE THE ANSWER. Commissioner and Inconsolata are the faces
     these items are set in; measuring against the fallback stack gives heights
     that are wrong by a line as soon as the real fonts swap in, and the pins
     would be permanently off by that much. `document.fonts.ready` is the only
     event that reliably lands after the swap. Guarded because the Font Loading
     API is not everywhere, and its absence just means the first measurement
     stands. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(schedule);
  }

  /* And the illustrations are CSS masks on boxes with a declared width and
     aspect-ratio, so they occupy their final space before the file has loaded
     and cannot reflow an item afterwards — there is deliberately no load
     listener here. If an item ever gains a real <img>, it needs one. */
})();
